import * as React from "react"
import styled, { css } from "styled-components"
import styledCss from "@styled-system/css"
import { Icon, IconTypes } from "../Icon"
import {
	zIndex,
	ZIndexProps,
	variant,
	ResponsiveValue,
	display,
	DisplayProps,
} from "styled-system"
import { fontWeight, scale, colors } from "../../styles"
import { Link, LinkProps } from "react-router-dom"
import { ColorKeys } from "../../styles/constants"
import {
	visibility,
	VisibilityProps,
	space,
	SpaceProps,
	color,
	ColorProps,
	border,
	BorderProps,
	typography,
	TypographyProps,
} from "../../styles/styled-system"

export type ButtonVariant = "navy" | "white" | "ghost" | "ghost-navy" | "purple" | "light-blue"
export type ButtonSize = "normal" | "small" | "medium" | "compact"
export type ButtonIconPosition = "left" | "right"
export interface ButtonProps
	extends TypographyProps,
	SpaceProps,
	ZIndexProps,
	VisibilityProps,
	ColorProps,
	BorderProps,
	DisplayProps {
	variant?: ButtonVariant
	icon?: IconTypes
	disabled?: boolean
	iconPosition?: ButtonIconPosition
	size?: ResponsiveValue<ButtonSize>
	children?: React.ReactNode
}

export interface ButtonComponent {
	/**
	 * Anchor-tag
	 */
	Anchor: typeof ButtonAnchor
	/**
	 * React DOM router link
	 */
	Link: typeof ButtonLink
}

export const ButtonNavyStyles = styledCss({
	backgroundColor: "navy",
	color: "white",
	"&:hover:not([disabled])": {
		backgroundColor: "hoverBlue",
	},
	"&:focus": {
		outline: `4px solid ${colors.blue100}`,
	},
})

const ButtonSizeNormal = {
	fontSize: scale.px(1.75),
	lineHeight: scale.px(2.75),
	padding: `${scale.px(1.5)} ${scale.px(3.5)}`,
}

export const ButtonSizeNormalStyles = styledCss(ButtonSizeNormal)

export const ButtonStyles = css<ButtonProps>`
	display: inline-flex;
	align-items: center;
	text-decoration: none;
	font-weight: ${fontWeight.bold};
	transition: background-color 0.25s, height 0.2s ease;
	text-align: center;
	justify-content: center;
	border-radius: ${scale.px(5)};
	text-transform: uppercase;
	border: 1px solid transparent;
	letter-spacing: 1px;
	${color}
	${border}
	${typography}
	${space}
	${zIndex}
	${visibility}
	${display}

	&:disabled {
		cursor: default;
		opacity: 50%;
	}

	&:hover {
		svg,
		path {
			fill: ${(props) => props.variant === "white" && "white"};
		}
	}

	${variant({
	variants: {
		navy: ButtonNavyStyles,
		white: styledCss({
			backgroundColor: "white",
			color: "navy",
			borderColor: "navy",
			"&:hover:not([disabled])": {
				backgroundColor: "hoverBlue",
				color: "white",
			},
			"&:focus": {
				outline: `4px solid ${colors.blue100}`,
			},
		}),
		ghost: styledCss({
			backgroundColor: "transparent",
			color: "navy",
			borderColor: "blue33",
			"&:hover:not([disabled])": {
				borderColor: "hoverBlue",
			},
			"&:focus": {
				outline: `4px solid ${colors.blue100}`,
			},
		}),
		"ghost-navy": styledCss({
			backgroundColor: "transparent",
			color: "navy",
			borderColor: "navy",
			"&:hover:not([disabled])": {
				borderColor: "hoverBlue",
			},
			"&:focus": {
				outline: `4px solid ${colors.blue100}`,
			},
		}),
		"light-blue": styledCss({
			backgroundColor: `${colors.blue11}`,
			color: "navy",
			"&:hover:not([disabled])": {
				backgroundColor: `${colors.blue33}`,
			},
			"&:focus": {
				outline: `4px solid ${colors.blue100}`,
			},
		}),
		purple: styledCss({
			backgroundColor: "purple33",
			color: "navy",
			borderColor: "transparent",
			"&:hover:not([disabled])": {
				borderColor: "purple100",
			},
			"&:focus": {
				outline: `4px solid ${colors.blue100}`,
			},
		}),
	},
})}

	${variant<{
	fontSize: string
	lineHeight: string
	padding: string
	width?: string
}>({
	prop: "size",
	variants: {
		normal: ButtonSizeNormal,
		small: {
			fontSize: scale.px(1.5),
			padding: `${scale.px(0.5)} ${scale.px(2)}`,
			lineHeight: scale.px(3),
		},
		medium: {
			fontSize: scale.px(1.5),
			padding: `${scale.px(1)} ${scale.px(2)}`,
			lineHeight: scale.px(3),
		},
		compact: {
			fontSize: scale.px(1.5),
			lineHeight: scale.px(3),
			padding: `${scale.px(0.5)} ${scale.px(3.5)}`,
			width: scale.px(20.5),
		},
	},
})};
`
// Stop attr in list from rendering to component HTML
const withConfig = {
	shouldForwardProp: (prop: any, defaultValidatorFn: any) =>
		![
			"lineHeight",
			"fontWeight",
			"fontSize",
			"display",
			"mr",
			"whitespace",
			"textColor",
			"variant",
		].includes(prop) && defaultValidatorFn(prop),
}

const ButtonBase: React.FC<
	{
		component: any
	} & ButtonProps
> = ({ icon, iconPosition, children, component: Component, ...rest }) => (
	<Component {...rest}>
		<>
			{icon && iconPosition === "left" && (
				<Icon
					icon={icon}
					mt={-0.25}
					ml={-0.25}
					mr={0.5}
					size={3}
					color={(rest.textColor as ColorKeys) || "white"}
				/>
			)}
			{children}
			{icon && iconPosition === "right" && (
				<Icon
					icon={icon}
					mt={-0.25}
					mr={-0.25}
					ml={0.5}
					size={3}
					color={(rest.textColor as ColorKeys) || "white"}
				/>
			)}
		</>
	</Component>
)

const defaultProps: ButtonProps = {
	variant: "navy",
	disabled: false,
	iconPosition: "left",
	size: "normal",
}

/**
 * Button element
 */
const ButtonStyled = styled.button.withConfig(withConfig)`
	${ButtonStyles}
`

export interface ButtonButtonProps
	extends ButtonProps,
	Omit<React.HTMLProps<HTMLButtonElement>, "size"> { }

const Button: React.FC<ButtonButtonProps> & ButtonComponent = (props) => (
	<ButtonBase component={ButtonStyled} {...props} />
)

Button.defaultProps = defaultProps

/**
 * Anchor element
 */
const AnchorStyled = styled.a.withConfig(withConfig)`
	${ButtonStyles}
`
const ButtonAnchor: React.FC<
	ButtonProps & Omit<React.HTMLProps<HTMLAnchorElement>, "size">
> = (props) => <ButtonBase component={AnchorStyled} {...props} />

Button.Anchor = ButtonAnchor
Button.Anchor.defaultProps = defaultProps

/**
 * React Router Link
 */
const LinkStyled = styled(Link).withConfig(withConfig)`
	${ButtonStyles}
`

const ButtonLink: React.FC<ButtonProps & LinkProps> = (props) => (
	<ButtonBase component={LinkStyled} {...props} />
)
Button.Link = ButtonLink
Button.Link.defaultProps = defaultProps

export { Button }
